import React, { useState, useEffect } from 'react';
import { Box, Text, Flex, Card, Badge } from '@radix-ui/themes';
import CircleProgress from '../CircleProgress';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { reportService } from '../../../services/reportService';
import { toast } from 'react-toastify';

const StudentQuizReport = ({ studentId, quizId }) => {
    const [selectedAttemptType, setSelectedAttemptType] = useState('last');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quizData, setQuizData] = useState(null);

    useEffect(() => {
        fetchQuizData();
    }, [quizId, studentId]);

    const fetchQuizData = async () => {
        try {
            setLoading(true);
            const response = await reportService.getStudentQuizReport(studentId, quizId);
            console.log('quiz data response>>>>>', response);
            setQuizData(response);
        } catch (error) {
            console.error('Error fetching quiz data:', error);
            toast.error('Failed to load quiz details');
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box p="4">
                <Text>Loading quiz details...</Text>
            </Box>
        );
    }

    if (error || !quizData) {
        return (
            <Box p="4">
                <Text color="red">Failed to load quiz details</Text>
            </Box>
        );
    }

    if (!quizData.quiz_attempts || quizData.quiz_attempts.length === 0) {
        return (
            <Box p="4">
                <Text color="gray">No attempts found for this quiz</Text>
            </Box>
        );
    }

    const firstAttempt = quizData.quiz_attempts.find(attempt => attempt.first_attempt);
    const lastAttempt = quizData.quiz_attempts[quizData.quiz_attempts.length - 1];
    const selectedAttempt = selectedAttemptType === 'first' ? firstAttempt : lastAttempt;

    return (
        <Box p="4">
            {/* Attempt Selector */}
            <Flex gap="2" mb="4">
                <Box 
                    onClick={() => setSelectedAttemptType('first')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: selectedAttemptType === 'first' ? 'var(--accent-3)' : 'transparent',
                    }}
                >
                    <Text size="2">First Attempt</Text>
                </Box>
                <Box 
                    onClick={() => setSelectedAttemptType('last')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: selectedAttemptType === 'last' ? 'var(--accent-3)' : 'transparent',
                    }}
                >
                    <Text size="2">Last Attempt</Text>
                </Box>
            </Flex>

            {/* Quiz Summary */}
            <Card mb="4">
                <Flex align="center" gap="4">
                    <CircleProgress
                        progress={selectedAttempt.percentage}
                        size={100}
                        label={`${selectedAttempt.percentage}%`}
                        labelSize="4"
                    />
                    <Flex direction="column" gap="2">
                        <Text size="5" weight="bold">Quiz Summary</Text>
                        <Flex gap="3">
                            <Badge size="2">
                                Questions: {quizData.questions.length}
                            </Badge>
                            <Badge size="2">
                                Score: {selectedAttempt.score}/{selectedAttempt.total_score}
                            </Badge>
                            <Badge size="2">
                                Time: {selectedAttempt.time_taken}s
                            </Badge>
                        </Flex>
                        <Flex gap="2">
                            <Badge color={selectedAttempt.vocabulary_score >= 70 ? 'green' : 'orange'}>
                                Vocabulary: {selectedAttempt.vocabulary_score}%
                            </Badge>
                            <Badge color={selectedAttempt.concept_score >= 70 ? 'green' : 'orange'}>
                                Concept: {selectedAttempt.concept_score}%
                            </Badge>
                            <Badge color={selectedAttempt.application_score >= 70 ? 'green' : 'orange'}>
                                Application: {selectedAttempt.application_score}%
                            </Badge>
                        </Flex>
                    </Flex>
                </Flex>
            </Card>

            {/* Questions List */}
            <Flex direction="column" gap="3">
                {quizData.questions.map((question, index) => {
                    const attempt = selectedAttempt.question_attempts.find(
                        qa => qa.question_id === question.id
                    );
                    const isCorrect = attempt?.is_correct;

                    return (
                        <Card key={question.id}>
                            <Flex justify="between" align="center" gap="3">
                                <Flex direction="column" gap="2" style={{ flex: 1 }}>
                                    <Flex align="center" gap="2">
                                        <Text weight="bold">Question {index + 1}</Text>
                                        <Flex gap="1" wrap="wrap" style={{ maxWidth: '200px' }}>
                                        {JSON.parse(question.core_competencies).map((comp, i) => (
                                            <Badge key={i} size="1" color="blue">
                                                {comp}
                                            </Badge>
                                        ))}
                                        </Flex>
                                        
                                    </Flex>
                                    <Text size="2">{question.text}</Text>
                                    <Text size="2" color="gray">
                                        Answer: {attempt?.answer?.text}
                                    </Text>
                                    <Text size="2" color="gray">
                                        Time taken: {attempt?.time_taken}s
                                    </Text>
                                </Flex>
                                <Flex>
                                    {isCorrect ? 
                                        <CheckCircledIcon width={40} height={40} style={{ color: 'var(--green-9)' }} /> : 
                                        <CrossCircledIcon width={40} height={40} style={{ color: 'var(--red-9)' }} />
                                    }
                                </Flex>
                                
                            </Flex>
                        </Card>
                    );
                })}
            </Flex>
        </Box>
    );
};

export default StudentQuizReport;
